/**
 * PersonnelTab — small CRUD for Drivers + Vehicles.
 *
 * These feed the <datalist> autocomplete on the Delivery Run editor so
 * operators can pick from a known roster while still being free to type
 * anything (the columns on delivery_routes remain free-text).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { User, Car, Plus, Edit2, Trash2, Archive, ArchiveRestore } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function DriverEditor({ open, onClose, initial, onSaved }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setPhone(initial?.phone || "");
      setNotes(initial?.notes || "");
    }
  }, [open, initial]);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Driver name is required");
      return;
    }
    setBusy(true);
    try {
      const body = { name: name.trim(), phone: phone.trim() || null, notes: notes.trim() || null };
      if (initial?.id) {
        await axios.patch(`${API}/drivers/${initial.id}`, body, { headers: auth() });
        toast.success("Driver updated");
      } else {
        await axios.post(`${API}/drivers`, body, { headers: auth() });
        toast.success("Driver added");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save driver");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="driver-editor-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit driver" : "Add driver"}</DialogTitle>
          <DialogDescription>
            Drivers added here appear as suggestions on Delivery Runs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
              data-testid="driver-name-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="04xx xxx xxx"
              data-testid="driver-phone-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              data-testid="driver-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="driver-save-button"
            className="bg-brand-navy text-white hover:bg-brand-navy-hover">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VehicleEditor({ open, onClose, initial, onSaved }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel(initial?.label || "");
      setDescription(initial?.description || "");
      setNotes(initial?.notes || "");
    }
  }, [open, initial]);

  const save = async () => {
    if (!label.trim()) {
      toast.error("Vehicle label is required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        label: label.trim(),
        description: description.trim() || null,
        notes: notes.trim() || null,
      };
      if (initial?.id) {
        await axios.patch(`${API}/vehicles/${initial.id}`, body, { headers: auth() });
        toast.success("Vehicle updated");
      } else {
        await axios.post(`${API}/vehicles`, body, { headers: auth() });
        toast.success("Vehicle added");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save vehicle");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="vehicle-editor-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit vehicle" : "Add vehicle"}</DialogTitle>
          <DialogDescription>
            Vehicles added here appear as suggestions on Delivery Runs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Label / Rego *</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ABC123"
              data-testid="vehicle-label-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. White Toyota HiAce" data-testid="vehicle-description-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              data-testid="vehicle-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="vehicle-save-button"
            className="bg-brand-navy text-white hover:bg-brand-navy-hover">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PersonnelTab() {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  const [driverEditor, setDriverEditor] = useState({ open: false, initial: null });
  const [vehicleEditor, setVehicleEditor] = useState({ open: false, initial: null });
  const [confirmDelete, setConfirmDelete] = useState(null); // {kind:'driver'|'vehicle', id, label}

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, vRes] = await Promise.all([
        axios.get(`${API}/drivers?include_archived=${showArchived}`, { headers: auth() }),
        axios.get(`${API}/vehicles?include_archived=${showArchived}`, { headers: auth() }),
      ]);
      setDrivers(dRes.data || []);
      setVehicles(vRes.data || []);
    } catch (e) {
      toast.error("Failed to load personnel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showArchived]);

  const toggleDriverArchived = async (d) => {
    try {
      await axios.patch(`${API}/drivers/${d.id}`, { is_archived: !d.is_archived }, { headers: auth() });
      toast.success(d.is_archived ? "Driver restored" : "Driver archived");
      load();
    } catch (e) {
      toast.error("Failed to update driver");
    }
  };

  const toggleVehicleArchived = async (v) => {
    try {
      await axios.patch(`${API}/vehicles/${v.id}`, { is_archived: !v.is_archived }, { headers: auth() });
      toast.success(v.is_archived ? "Vehicle restored" : "Vehicle archived");
      load();
    } catch (e) {
      toast.error("Failed to update vehicle");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      const url = confirmDelete.kind === "driver"
        ? `${API}/drivers/${confirmDelete.id}`
        : `${API}/vehicles/${confirmDelete.id}`;
      await axios.delete(url, { headers: auth() });
      toast.success("Deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="personnel-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Manage drivers and vehicles. These power the autocomplete on Delivery Runs.
        </p>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="personnel-show-archived"
          />
          Show archived
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Drivers */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-brand-navy" />
                <h3 className="font-display text-lg font-bold text-brand-navy">Drivers</h3>
                <span className="text-xs text-muted-foreground">({drivers.length})</span>
              </div>
              <Button size="sm" onClick={() => setDriverEditor({ open: true, initial: null })}
                data-testid="add-driver-button"
                className="bg-brand-navy text-white hover:bg-brand-navy-hover">
                <Plus className="w-4 h-4 mr-1" /> Add driver
              </Button>
            </div>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : drivers.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-4">
                No drivers yet. Add one to use it in Run autocomplete.
              </p>
            ) : (
              <ul className="divide-y divide-neutral-200">
                {drivers.map((d) => (
                  <li key={d.id}
                      data-testid={`driver-row-${d.id}`}
                      className={`py-3 flex items-center justify-between gap-3 ${d.is_archived ? "opacity-60" : ""}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {d.name}
                        {d.is_archived && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-500">archived</span>
                        )}
                      </div>
                      {(d.phone || d.notes) && (
                        <div className="text-xs text-muted-foreground truncate">
                          {d.phone && <span>{d.phone}</span>}
                          {d.phone && d.notes && <span className="mx-1">·</span>}
                          {d.notes && <span>{d.notes}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost"
                              onClick={() => setDriverEditor({ open: true, initial: d })}
                              data-testid={`edit-driver-${d.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost"
                              onClick={() => toggleDriverArchived(d)}
                              data-testid={`archive-driver-${d.id}`}>
                        {d.is_archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                      </Button>
                      <Button size="icon" variant="ghost"
                              onClick={() => setConfirmDelete({ kind: "driver", id: d.id, label: d.name })}
                              data-testid={`delete-driver-${d.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Vehicles */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-brand-navy" />
                <h3 className="font-display text-lg font-bold text-brand-navy">Vehicles</h3>
                <span className="text-xs text-muted-foreground">({vehicles.length})</span>
              </div>
              <Button size="sm" onClick={() => setVehicleEditor({ open: true, initial: null })}
                data-testid="add-vehicle-button"
                className="bg-brand-navy text-white hover:bg-brand-navy-hover">
                <Plus className="w-4 h-4 mr-1" /> Add vehicle
              </Button>
            </div>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : vehicles.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-4">
                No vehicles yet. Add one to use it in Run autocomplete.
              </p>
            ) : (
              <ul className="divide-y divide-neutral-200">
                {vehicles.map((v) => (
                  <li key={v.id}
                      data-testid={`vehicle-row-${v.id}`}
                      className={`py-3 flex items-center justify-between gap-3 ${v.is_archived ? "opacity-60" : ""}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {v.label}
                        {v.is_archived && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-neutral-500">archived</span>
                        )}
                      </div>
                      {(v.description || v.notes) && (
                        <div className="text-xs text-muted-foreground truncate">
                          {v.description && <span>{v.description}</span>}
                          {v.description && v.notes && <span className="mx-1">·</span>}
                          {v.notes && <span>{v.notes}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost"
                              onClick={() => setVehicleEditor({ open: true, initial: v })}
                              data-testid={`edit-vehicle-${v.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost"
                              onClick={() => toggleVehicleArchived(v)}
                              data-testid={`archive-vehicle-${v.id}`}>
                        {v.is_archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                      </Button>
                      <Button size="icon" variant="ghost"
                              onClick={() => setConfirmDelete({ kind: "vehicle", id: v.id, label: v.label })}
                              data-testid={`delete-vehicle-${v.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <DriverEditor
        open={driverEditor.open}
        initial={driverEditor.initial}
        onClose={() => setDriverEditor({ open: false, initial: null })}
        onSaved={load}
      />
      <VehicleEditor
        open={vehicleEditor.open}
        initial={vehicleEditor.initial}
        onClose={() => setVehicleEditor({ open: false, initial: null })}
        onSaved={load}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="personnel-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {confirmDelete?.kind}?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{confirmDelete?.label}&quot; will be permanently removed. Existing Delivery Runs that
              already reference this name keep their free-text value — only the autocomplete
              suggestion goes away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="personnel-confirm-delete-button"
              className="bg-red-600 text-white hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
